/**
 * src/api/cases.js
 *
 * All API calls for the Cases page: metric cards, the paginated case list, and the
 * team/priority filter option lists. Bearer token is attached automatically.
 */

import { authedJson, authedFetch, raiseForStatus } from './http'

const apiGet = path => authedJson('GET', path)
const apiPostJson = (path, body) => authedJson('POST', path, body)

/**
 * Metric cards for the top of the Cases page, scoped by the active filters.
 * POST /api/cases/metrics
 *
 * Returns a fixed 4-element array of metric objects:
 *   { name, value, description, cardtype: 'number'|'bar'|'graph'|'error',
 *     icon, caption?, unit?, bar_scale?, error_ref?, evidence_header?, evidence? }
 * For 'graph'/'bar', value is an object (or JSON string) the card parses; for
 * 'bar', bar_scale: 'max' widths bars relative to the largest value. A metric that
 * failed to generate comes back as an 'error' card carrying an error_ref (never
 * dropped), so a partial failure stays visible and traceable.
 *
 * @param {object}  [opts]
 * @param {string} [opts.filterTeam]     — team name to scope by
 * @param {string} [opts.filterPriority] — priority label to scope by
 * @returns {Promise<Array<object>>}
 */
export async function fetchCaseMetrics({ filterTeam, filterPriority } = {}) {
  return apiPostJson('/cases/metrics', {
    filter_team: filterTeam || null,
    filter_priority: filterPriority || null,
  })
}

/**
 * The distinct priority labels for the Priority filter dropdown.
 * GET /api/cases/priorities
 * @returns {Promise<string[]>}
 */
export async function fetchPriorities() {
  return apiGet('/cases/priorities')
}

/**
 * The full team list for the Team filter dropdown.
 * GET /api/teams
 * @returns {Promise<Array<{ id: number, name: string }>>}
 */
export async function fetchTeams() {
  return apiGet('/teams')
}

/**
 * A page of case summaries for the case-card list.
 * POST /api/cases/list
 *
 * Opaque forward-cursor pagination: pass `cursor: null` for the first page, then feed
 * back the `next_cursor` from the previous response to page forward. `next_cursor` is
 * null once the last page has been reached. The cursor is opaque — for a browse (empty
 * search) it encodes the keyset key; for an active search it encodes the text offset.
 *
 * When `search` is non-empty the response also carries a `pinned` block: capped
 * ID/number matches shown page-1 only (with `pinned_more` = count beyond the cap),
 * above the relevance-ranked `items` body. `pinned` items may also appear in `items`
 * (a case can match by number and by text) — dedupe by `number`, preferring pinned.
 *
 * @param {object}  opts
 * @param {string|null} [opts.cursor]    — opaque forward cursor (null = first page)
 * @param {number}  opts.count           — page size (default 20)
 * @param {string} [opts.filterTeam]     — team name to filter by
 * @param {string} [opts.filterPriority] — priority label to filter by
 * @param {string} [opts.filterIssue]    — issue (Symptom) name from the Top Issues dropdown
 * @param {string} [opts.search]         — free-text: number prefix, title/agent terms
 * @returns {Promise<{ pinned?: Array<object>, pinned_more?: number, items: Array<object>, next_cursor: string|null }>} one page
 */
export async function fetchCases({ cursor = null, count = 20, filterTeam, filterPriority, filterIssue, search } = {}) {
  return apiPostJson('/cases/list', {
    cursor,
    count,
    filter_team: filterTeam || null,
    filter_priority: filterPriority || null,
    filter_issue: filterIssue || null,
    search: search || null,
  })
}

/**
 * Full detail payload for one case, for the case detail page.
 * GET /api/cases/{ticket_id}
 *
 * Returns the object documented in gaussiq-devs/docs/design/cases/cases.md
 * (number, title, summary, symptoms/problems, root_cause(+detail), ownership_timeline,
 * routing_factors, skills, the right-rail meters, etc.). Fields absent on the ticket come
 * back null; routing_factors is stubbed this pass. response_plan is a markdown string (or
 * null/absent when no plan exists for the ticket). skills is an array of
 * { name, type, reason } drawn from the ticket's REQUIRES_SKILL edges (empty array if none).
 *
 * @param {string} ticketId — the case number/id (e.g. "ACM-26173")
 * @returns {Promise<object>}
 */
export async function fetchCaseDetail(ticketId) {
  return apiGet(`/cases/${encodeURIComponent(ticketId)}`)
}

/**
 * The generated post-mortem PDF for one case, as a Blob.
 * GET /api/cases/{ticket_id}/report
 *
 * Returns the raw application/pdf bytes rather than a URL because the endpoint is
 * Bearer-authenticated: a bare window.open() on it sends no Authorization header
 * and gets a 401. The caller opens the Blob via URL.createObjectURL instead.
 *
 * A 404 means the case has no report yet — the post-mortem job may simply never
 * have run for it. That is a state worth rendering, not a failure; it arrives as
 * an Error with `status === 404`, the same shape the JSON helpers raise.
 *
 * @param {string} ticketId — the case number/id (e.g. "00188970")
 * @returns {Promise<Blob>} the PDF
 */
/**
 * Whether a post-mortem exists for this case, is being generated, or failed.
 * GET /api/cases/{ticket_id}/report/status
 *
 * Drives which of the three post-mortem button states the detail page shows:
 *   { status: 'none' }                    — nothing generated yet
 *   { status: 'generating' }              — a job is running; poll this
 *   { status: 'done' }                    — fetchCaseReport() will return a PDF
 *   { status: 'error', message: string }  — generation failed
 *
 * @param {string} ticketId
 * @returns {Promise<{ status: 'none'|'generating'|'done'|'error', message?: string }>}
 */
export async function fetchCaseReportStatus(ticketId) {
  return apiGet(`/cases/${encodeURIComponent(ticketId)}/report/status`)
}

/**
 * Kick off a post-mortem generation job for one case.
 * POST /api/carl/launch_ai_job
 *
 * Fire-and-poll. Resolves with 202 as soon as the job is registered, not when it
 * finishes — the run itself takes minutes and continues server-side. By the time
 * this resolves the report is already marked 'generating', so switch to polling
 * fetchCaseReportStatus() for the outcome; failures surface there, not here.
 *
 * @param {string} ticketId — becomes the job's tag, and the report's identity
 * @returns {Promise<{ job_id: number, status: string, tag: string }>}
 */
export async function launchPostMortemJob(ticketId) {
  return apiPostJson('/carl/launch_ai_job', {
    tag: ticketId,
    job_name: 'post_mortem',
    job_type: 'report',
  })
}

export async function fetchCaseReport(ticketId) {
  const res = await authedFetch(`/cases/${encodeURIComponent(ticketId)}/report`)
  if (!res.ok) await raiseForStatus(res)
  return res.blob()
}
